import React, { useState } from "react";
import { Box, Button, CircularProgress, InputBase, Tooltip, makeStyles, useTheme } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import moment from "moment-timezone";

// Shared look for the Spam Review console (AGN-206). Minimal: structure comes
// from type, whitespace and hairlines. Colours come from the superadmin theme
// (src/themes/default.js); only the panel background and the tints are literals.

export const PANEL_BG = "rgb(251,251,251)"; // console ProjectsLayout background
export const SELECTED_BG = "rgba(10,71,76,.06)";
export const ERROR_TINT = "rgba(244,67,54,.08)";
export const PENDING_TINT = "rgba(230,81,0,.08)";
export const SECONDARY_TINT = "rgba(254,192,0,.16)";
export const GREY_TINT = "rgba(0,0,0,.05)";
export const RESOLVED_TINT = "rgba(76,175,80,.10)";

// Theme accessors (pending / resolved are AiSensy palette extensions).
export const pal = (theme) => {
  const p = theme.palette;
  return {
    primary: p.primary.main,
    error: p.error.main,
    pending: (p.pending && p.pending.main) || p.warning.main,
    resolved: (p.resolved && p.resolved.main) || p.success.main,
    secondary: p.secondary.main,
    text: p.text.primary,
    text2: p.text.secondary,
    hint: p.text.hint,
    line: p.grey[300],
    line2: p.grey[200],
  };
};

// Theme colours as a plain object, for inline styles.
export const usePal = () => pal(useTheme());

export const SUBJECTS = [
  { key: "whatsapp_template", label: "Templates", filter: "Templates", single: "template", plural: "templates" },
  { key: "meta_ad", label: "Ads", filter: "Ads", single: "ad", plural: "ads" },
  { key: "business_profile", label: "Profile", filter: "Profile", single: "profile change", plural: "profile changes" },
];
export const subjectOf = (key) => SUBJECTS.find((s) => s.key === key) || SUBJECTS[0];

export const CONSEQUENCE = {
  dismiss: "Every open item of this project is marked OK. Nothing is suspended.",
  dismissNothingOpen: "Nothing is open on this project. Nothing is suspended.",
  suspend_project: "This project can't create templates or send messages until reinstated.",
  suspend_account: "Every project of this owner is suspended and all team members are logged out until reinstated.",
  reinstate: "Restores sending and template creation, and re-enables the logins the suspension disabled.",
  delete: "The template is deleted from the customer's WhatsApp account. This can't be undone.",
  reject: "The template stays unsent and the customer sees it as rejected.",
};

export const errMsg = (e, fallback) => e?.response?.data?.message || e?.message || fallback;

export const fmtDate = (v) => (v ? moment(v).format("DD MMM YYYY, HH:mm") : "—");
export const fmtDay = (v) => (v ? moment(v).format("DD MMM YYYY") : "—");
export const fromNow = (v) => (v ? moment(v).fromNow() : "—");

// Compact relative time for dense lines: 12m, 5h, 3d, 04 Oct.
export const shortAgo = (v) => {
  if (!v) return "";
  const mins = moment().diff(moment(v), "minutes");
  if (mins < 1) return "now";
  if (mins < 60) return `${mins}m`;
  if (mins < 60 * 24) return `${Math.floor(mins / 60)}h`;
  if (mins < 60 * 24 * 30) return `${Math.floor(mins / 1440)}d`;
  return moment(v).format("DD MMM");
};

// Age as a big number: 4d, 3mo, 2y.
export const shortAge = (v) => {
  if (!v) return "—";
  const d = moment().diff(moment(v), "days");
  if (d < 1) return "<1d";
  if (d < 60) return `${d}d`;
  if (d < 730) return `${Math.floor(d / 30)}mo`;
  return `${Math.floor(d / 365)}y`;
};
export const ageDays = (v) => (v ? moment().diff(moment(v), "days", true) : null);

export const titleCase = (s) =>
  String(s || "")
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

export const sentenceCase = (s) => {
  const t = String(s || "").replace(/[_-]+/g, " ").trim().toLowerCase();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : "";
};

const NUM_WORDS = ["no", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten"];
export const numWord = (n) => NUM_WORDS[n] || String(n);
export const plural = (n, one, many) => `${n} ${n === 1 ? one : many || `${one}s`}`;
export const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

export const shortId = (id) => {
  const s = String(id || "");
  return s.length > 12 ? `${s.slice(0, 8)}…${s.slice(-4)}` : s;
};

export const hostOf = (url) => {
  if (!url) return null;
  try {
    const u = String(url).replace(/\{\{\s*\d+\s*\}\}/g, "x");
    return new URL(/^[a-z]+:\/\//i.test(u) ? u : `https://${u}`).hostname.replace(/^www\./, "");
  } catch (e) {
    return null;
  }
};
// Strip the scheme for display: sbi-kyc-update.xyz/login
export const bareUrl = (url) => String(url || "").replace(/^[a-z]+:\/\//i, "").replace(/^www\./, "");
// AiSensy's own click-tracking redirector — never the real destination.
export const TRACKER_HOSTS = new Set(["api-wa.co"]);

const CONTENT_LABELS = {
  illegal: "Illegal content",
  lottery_or_chance_based_promotion: "Lottery or prize draw",
  impersonation: "Impersonation",
  uncertain: "Content unsure",
  benign: "Legit",
  ordinary_commercial_promotion: "Ordinary promotion",
};
export const humanizeLabel = (key) => CONTENT_LABELS[key] || sentenceCase(key);

// Reviewer labels (the correct content label, sent with a decision).
export const LEGIT_OPTIONS = [
  { value: "benign", label: "Legit — transactional", word: "transactional" },
  { value: "ordinary_commercial_promotion", label: "Legit — promotion", word: "promotion" },
];
export const HARM_OPTIONS = [
  { value: "spam", label: "Spam" },
  { value: "phishing", label: "Phishing" },
  { value: "gambling", label: "Gambling" },
  { value: "lottery_or_chance_based_promotion", label: "Lottery or prize draw" },
  { value: "illegal", label: "Illegal content" },
];
const isHarm = (l) => HARM_OPTIONS.some((o) => o.value === l);
// Short word for an outcome line: "transactional", "promotion", "phishing".
export const labelWord = (l) =>
  (LEGIT_OPTIONS.find((o) => o.value === l) || {}).word ||
  ((HARM_OPTIONS.find((o) => o.value === l) || {}).label || humanizeLabel(l)).toLowerCase();
// Promotion when the template is a marketing one, else transactional.
export const legitFor = (category) =>
  String(category || "").toUpperCase() === "MARKETING" ? "ordinary_commercial_promotion" : "benign";
export const harmDefault = (label) => (isHarm(label) ? label : "");

const SIGNAL_LABELS = {
  account_suspension_threat: "threatens account suspension",
  brand_domain_mismatch: "link domain doesn't match the brand",
  credential_or_document_request: "asks for credentials or documents",
  payment_or_fee_request: "asks for a payment or fee",
  delivery_verification_lure: "fake delivery verification",
  lottery_or_prize_mechanic: "prize or lottery mechanic",
  bulk_lead_sale: "sells contact lists",
  privacy_invasive_surveillance_offer: "spyware / surveillance offer",
  review_incentive: "pays for reviews",
  unrealistic_earnings: "unrealistic earnings",
  apk_download: "asks to install an APK",
};
export const humanizeSignal = (id) => SIGNAL_LABELS[id] || sentenceCase(id).toLowerCase();
const POLICY_NAMES = {
  payday_p2p_lending_debt_collection: "payday / P2P lending",
  real_money_gambling: "real-money gambling",
  illegal_products_or_services: "illegal products",
  medical_healthcare_products: "medical / healthcare products",
};
export const humanizePolicy = (id) => POLICY_NAMES[id] || sentenceCase(id).toLowerCase();
export const POLICY_IDS = Object.keys(POLICY_NAMES);

// What the checker thinks of the content, in a few words.
const VERDICT_PHRASE = {
  benign: "Looks legit",
  ordinary_commercial_promotion: "Ordinary promotion",
  lottery_or_chance_based_promotion: "Lottery or prize draw",
  uncertain: "Unsure",
};
const verdictPhrase = (l) => VERDICT_PHRASE[l] || humanizeLabel(l);

const confidenceWord = (p, fallback) => {
  if (p != null && !Number.isNaN(p)) {
    if (p >= 0.95) return "very high";
    if (p >= 0.8) return "high";
    if (p >= 0.5) return "medium";
    return "low";
  }
  return fallback ? String(fallback).toLowerCase().replace(/_/g, " ") : "";
};

// Machine explanations ("jev: phishing (p=0.98)") carry the label and the
// confidence; readable ones are shown as they are.
export const isMachineText = (t) => /^\s*jev:/i.test(String(t || ""));

// "Looks legit · very high confidence", "Phishing · high confidence".
export const contentVerdict = (item) => {
  const content = item?.content || {};
  const raw = content.explanation;
  const m = (isMachineText(raw) && String(raw).match(/^\s*jev:\s*([a-z_]+)?\s*(?:\(\s*p\s*=\s*([\d.]+)\s*\))?/i)) || [];
  const label = content.primaryLabel || m[1];
  if (!label) return "No verdict";
  const conf = confidenceWord(m[2] != null ? parseFloat(m[2]) : null, content.confidence);
  return `${verdictPhrase(label)}${conf ? ` · ${conf} confidence` : ""}`;
};

// The model's own readable explanation, if it wrote one.
export const readableExplanation = (item) => {
  const raw = item?.content?.explanation;
  return raw && !isMachineText(raw) ? raw : "";
};

const FEATURE_LABELS = {
  owner_age_lt_1d: "Owner account under 1 day",
  owner_age_lt_7d: "Owner account under 7 days",
  owner_age_lt_180d: "Owner account 30–180 days",
  owner_age_gte_180d: "Owner account 180+ days",
  waba_age_lt_7d: "WABA activated under 7 days ago",
  waba_age_gt_90d: "WABA active 90+ days",
  paid_history: "Paid plan history",
  recharged_gte_5000: "Recharged ₹5,000+",
  email_verified: "Email verified",
  gstin_present: "GSTIN on file",
  fb_manager_verified: "Facebook business verified",
  display_name_verified: "Display name verified",
  quality_red: "Quality rating red",
  quality_yellow: "Quality rating yellow",
  waba_banned: "WABA banned",
  reject_ratio_gt_50pct: "Over half of templates rejected",
  clean_template_history: "Clean template history",
  blocked_last_30d: "Template blocked in last 30 days",
  identity_entity_strike: "Device struck on another account",
  partner_spam_rate_gt_2pct: "Partner spam rate above 2%",
  device_shared_by_many_accounts: "Device shared by many accounts",
  new_account_on_struck_device: "New account on a struck device",
  resubmission_burst: "Burst of rejected resubmissions",
};
export const humanizeFeature = (key) => FEATURE_LABELS[key] || sentenceCase(key);

export const DECISION_TEXT = { block: "High risk", review: "Needs a check", report: "FYI" };

// One place for every status word a reviewer sees (pills, sub-tabs, legend),
// with what it means and the internal term it maps to in logs and the API.
export const STATUS_HELP = {
  held: {
    label: "Held — not sent to Meta",
    short: "Held",
    hint: "Approve sends it to Meta; Reject means it is never sent.",
    help: "Not sent to Meta. The checker stopped this new template before submission. Approve sends it to Meta exactly as written; Reject means it is never sent and the customer sees it as rejected.",
    term: "block (held)",
    tab: "Blocked",
  },
  block: {
    label: "Sent to Meta — high risk",
    short: "High risk",
    hint: "Already submitted. Mark OK if it's fine, or delete the template.",
    help: "The checker judged this harmful (or a blocked Meta category) but the template had already gone to Meta, so it couldn't be held. Mark OK if it's fine, or delete the template.",
    term: "block",
    tab: "Blocked",
  },
  review: {
    label: "Sent to Meta — needs a check",
    short: "Needs a check",
    hint: "Already submitted. Mark OK if it's fine, or delete the template.",
    help: "Sent to Meta normally. The checker wasn't sure enough to stop it and wants a human to look. Mark OK if it's fine, or delete the template.",
    term: "review",
    tab: "Review",
  },
  report: {
    label: "Sent to Meta — FYI",
    short: "FYI",
    hint: "The checker was unsure. No action needed unless it looks wrong.",
    help: "Sent to Meta normally. The checker was unsure about the content, so it's only logged here. No action needed unless it looks wrong; marking it helps train the checker.",
    term: "report",
    tab: "Uncertain",
  },
  // Dismiss option (on by default): send held templates and those rejected earlier to Meta.
  restore: {
    label: "Restore all templates",
    short: "Restore",
    hint: "Sends held templates and templates rejected earlier to Meta as written.",
    help: "Held templates and templates that were held and then rejected here were never sent; the customer sees them as pending or rejected. Restore sends each one to Meta exactly as written, marks it OK and removes the strike a rejection added. Use it when they were blocked wrongly.",
    term: "dismiss + restoreRejected",
  },
  // Owner-level settings (apply to all of the owner's projects).
  aggregator: {
    label: "Aggregator",
    short: "Aggregator",
    help: "This owner is an aggregator: it sends on behalf of clients or lenders. Naming other brands isn't flagged; only real phishing, betting or illegal content is held. Applies to all of the owner's projects.",
    term: "senderType=aggregator",
  },
  service_provider: {
    label: "Service provider",
    short: "Service provider",
    help: "This owner is a service provider: it sends on behalf of its clients. Naming other brands isn't flagged; only real phishing, betting or illegal content is held. Applies to all of the owner's projects.",
    term: "senderType=service_provider",
  },
  whitelisted: {
    label: "Whitelisted",
    short: "Whitelisted",
    help: "All of this owner's projects skip spam screening: templates are never held or listed here. Links to known scam hosts are still blocked.",
    term: "whitelisted=true",
  },
  // Pending sub-tabs: each project sits in one, by its worst open decision.
  blockedTab: {
    label: "Blocked",
    help: "Accounts with a template the checker blocked — held from Meta if it was new, or already sent but judged high risk.",
    term: "block",
  },
  reviewTab: {
    label: "Review",
    help: "Accounts with templates sent to Meta that the checker wants a human to check. Nothing was held.",
    term: "review",
  },
  uncertainTab: {
    label: "Uncertain",
    help: "Accounts whose templates the checker was only unsure about. Sent to Meta and logged; act only if something looks wrong.",
    term: "report",
  },
  // Older backends: one tab for block and review together.
  blockedReviewTab: {
    label: "Blocked & review",
    help: "Accounts with a template the checker blocked (held from Meta, or sent but high risk) or wants a human to check.",
    term: "block + review",
  },
};
export const STATUS_ORDER = ["held", "block", "review", "report"];

// Colour for a decision dot / pill, a trust tier, a quality rating.
export const decisionColor = (c, d) =>
  ({ block: c.error, review: c.pending, report: c.hint }[d] || c.hint);
export const tierColor = (c, tier) =>
  ({ LOW: c.error, MEDIUM: c.pending, HIGH: c.resolved }[tier] || c.text2);
export const qualityColor = (c, q) =>
  ({ RED: c.error, YELLOW: c.pending, GREEN: c.resolved }[String(q || "").toUpperCase()] || c.text);

export const useUi = makeStyles((theme) => {
  const c = pal(theme);
  const btn = {
    textTransform: "none",
    fontSize: 12.5,
    fontWeight: 400,
    lineHeight: "18px",
    minWidth: 0,
    borderRadius: 8,
    whiteSpace: "nowrap",
  };
  return {
    sectionTitle: {
      fontSize: 11.5,
      textTransform: "uppercase",
      letterSpacing: ".06em",
      color: c.hint,
      marginBottom: 12,
    },
    section: { marginTop: 36 },
    muted: { color: c.hint, fontSize: 12 },
    text2: { color: c.text2 },
    textBtn: { ...btn, padding: "8px 12px", color: c.text2, "&:hover": { background: SELECTED_BG } },
    inlineBtn: {
      ...btn,
      padding: 0,
      color: c.primary,
      "&:hover": { background: "transparent", textDecoration: "underline" },
    },
    danger: { color: c.error },
    primaryText: { color: c.primary },
    filledDanger: {
      ...btn,
      fontWeight: 500,
      padding: "8px 14px",
      background: c.error,
      color: "#fff",
      "&:hover": { background: theme.palette.error.dark },
      "&.Mui-disabled": { color: "#fff", opacity: 0.6 },
    },
    filledPrimary: {
      ...btn,
      fontWeight: 500,
      padding: "8px 14px",
      background: c.primary,
      color: "#fff",
      "&:hover": { background: theme.palette.primary.dark },
      "&.Mui-disabled": { color: "#fff", opacity: 0.6 },
    },
    pill: {
      display: "inline-block",
      borderRadius: 6,
      padding: "1px 7px",
      fontSize: 11.5,
      fontWeight: 500,
      lineHeight: "18px",
      marginLeft: 8,
      verticalAlign: 2,
    },
    statusPill: { marginLeft: 0, cursor: "help", outline: "none", "&:focus-visible": { boxShadow: `0 0 0 2px ${c.line}` } },
    tip: { display: "block", fontSize: 12, lineHeight: 1.45, maxWidth: 300 },
    tipTerm: { display: "block", marginTop: 4, fontSize: 11, opacity: 0.7, fontFamily: "monospace" },
    confirm: {
      marginTop: 16,
      padding: "14px 16px",
      borderRadius: 8,
      background: PANEL_BG,
      border: `1px solid ${c.line2}`,
      maxWidth: 560,
    },
    note: {
      width: "100%",
      marginTop: 10,
      fontSize: 13,
      padding: "6px 10px",
      borderRadius: 8,
      border: `1px solid ${c.line}`,
      background: "#fff",
      color: c.text,
    },
    copy: { cursor: "pointer", "&:hover": { color: c.primary } },
    choices: { display: "flex", gap: 18, marginTop: 10, fontSize: 13, flexWrap: "wrap" },
    choice: {
      cursor: "pointer",
      background: "none",
      border: 0,
      padding: 0,
      font: "inherit",
      color: c.text2,
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      "&:hover": { color: c.text },
      "&:disabled": { cursor: "default", opacity: 0.6 },
    },
    choiceOn: { color: c.primary, fontWeight: 600, "&:hover": { color: c.primary } },
    dot: {
      width: 10,
      height: 10,
      borderRadius: "50%",
      border: `1px solid ${c.line}`,
      background: "#fff",
      boxSizing: "border-box",
    },
    dotOn: { border: `3px solid ${c.primary}` },
    check: {
      display: "flex",
      alignItems: "flex-start",
      gap: 8,
      marginTop: 10,
      fontSize: 13,
      lineHeight: 1.45,
      color: c.text,
      cursor: "pointer",
      "& input": { margin: "2px 0 0", accentColor: c.primary, cursor: "inherit" },
    },
    checkOff: { cursor: "default", opacity: 0.6 },
    field: { display: "flex", alignItems: "center", gap: 10, marginTop: 10, fontSize: 13, color: c.hint },
    select: {
      fontSize: 13,
      padding: "4px 8px",
      borderRadius: 8,
      border: `1px solid ${c.line}`,
      background: "#fff",
      color: c.text,
      font: "inherit",
      outline: "none",
      "&:focus": { borderColor: c.primary },
    },
  };
});

export function Section({ title, children, style }) {
  const ui = useUi();
  return (
    <Box className={ui.section} style={style}>
      <div className={ui.sectionTitle}>{title}</div>
      {children}
    </Box>
  );
}

export function Pill({ color, bg, children }) {
  const ui = useUi();
  return (
    <span className={ui.pill} style={{ color, background: bg }}>
      {children}
    </span>
  );
}

// Hover / focus explanation for a status word, with the internal term below.
export function HelpTip({ help, term, children }) {
  const ui = useUi();
  if (!help) return children;
  return (
    <Tooltip
      arrow
      enterDelay={250}
      title={
        <span className={ui.tip}>
          {help}
          {term ? <span className={ui.tipTerm}>internal: {term}</span> : null}
        </span>
      }
    >
      {children}
    </Tooltip>
  );
}

// Pill colours per status tone.
export const statusTone = (c, tone) =>
  ({
    held: { color: "#fff", bg: c.error },
    block: { color: c.error, bg: ERROR_TINT },
    review: { color: c.pending, bg: PENDING_TINT },
    report: { color: c.text2, bg: GREY_TINT },
    done: { color: c.resolved, bg: RESOLVED_TINT },
    bad: { color: c.error, bg: ERROR_TINT },
    sender: { color: c.primary, bg: SELECTED_BG },
    whitelisted: { color: c.resolved, bg: RESOLVED_TINT },
  }[tone] || { color: c.text2, bg: GREY_TINT });

// A status pill ({ label, tone, help, term }) that explains itself on hover / focus.
export function StatusPill({ status, style }) {
  const ui = useUi();
  const c = usePal();
  if (!status) return null;
  const t = statusTone(c, status.tone);
  return (
    <HelpTip help={status.help} term={status.term}>
      <span tabIndex={0} className={`${ui.pill} ${ui.statusPill}`} style={{ color: t.color, background: t.bg, ...style }}>
        {status.label}
      </span>
    </HelpTip>
  );
}

// Text-only action. `tone`: default (text2) | primary | danger.
export function TextAction({ tone, inline, className, ...props }) {
  const ui = useUi();
  const cls = [inline ? ui.inlineBtn : ui.textBtn, tone === "danger" ? ui.danger : "", tone === "primary" ? ui.primaryText : "", className || ""];
  return <Button disableRipple={inline} className={cls.join(" ")} {...props} />;
}

export function CopyText({ value, children, label = "Copy" }) {
  const ui = useUi();
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  const copy = (e) => {
    e.stopPropagation();
    try {
      navigator.clipboard.writeText(String(value)).then(
        () => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        },
        () => {}
      );
    } catch (err) {
      // clipboard unavailable (insecure context)
    }
  };
  return (
    <Tooltip title={copied ? "Copied" : `${label}: ${value}`} arrow>
      <span className={ui.copy} role="button" tabIndex={0} onClick={copy} onKeyDown={(e) => e.key === "Enter" && copy(e)}>
        {children || value}
      </span>
    </Tooltip>
  );
}

export function SkeletonLines({ lines = 3, widths }) {
  return (
    <Box>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} height={18} width={(widths && widths[i % widths.length]) || `${70 - (i % 3) * 15}%`} />
      ))}
    </Box>
  );
}

export function ErrorLine({ message, onRetry, style }) {
  const ui = useUi();
  return (
    <Box style={{ display: "flex", alignItems: "center", gap: 8, ...style }}>
      <span className={ui.danger} style={{ fontSize: 13 }}>
        {message}
      </span>
      {onRetry ? (
        <TextAction inline tone="primary" onClick={onRetry}>
          Retry
        </TextAction>
      ) : null}
    </Box>
  );
}

// Minimal in-place confirmation: consequence sentence, optional note, Confirm / Cancel.
// `children` sit between the sentence and the note (label choosers).
export function InlineConfirm({
  consequence,
  confirmLabel,
  danger,
  withNote = true,
  notePlaceholder = "Note (optional)",
  busy,
  confirmDisabled,
  error,
  onConfirm,
  onCancel,
  style,
  children,
}) {
  const ui = useUi();
  const [note, setNote] = useState("");
  return (
    <Box className={ui.confirm} style={style} role="alertdialog" onClick={(e) => e.stopPropagation()}>
      <div style={{ fontSize: 13 }}>{consequence}</div>
      {children}
      {withNote ? (
        <InputBase
          className={ui.note}
          placeholder={notePlaceholder}
          value={note}
          inputProps={{ maxLength: 500, "aria-label": notePlaceholder }}
          onChange={(e) => setNote(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !busy && !confirmDisabled) onConfirm(note.trim());
          }}
          disabled={busy}
        />
      ) : null}
      {error ? (
        <div className={ui.danger} style={{ fontSize: 12.5, marginTop: 8 }}>
          {error}
        </div>
      ) : null}
      <Box style={{ display: "flex", gap: 6, marginTop: 12, alignItems: "center" }}>
        <Button
          disableElevation
          className={danger ? ui.filledDanger : ui.filledPrimary}
          disabled={busy || confirmDisabled}
          onClick={() => onConfirm(note.trim())}
          startIcon={busy ? <CircularProgress size={12} color="inherit" /> : null}
        >
          {confirmLabel}
        </Button>
        <TextAction disabled={busy} onClick={onCancel}>
          Cancel
        </TextAction>
      </Box>
    </Box>
  );
}

// Quiet radio row: "Legit — transactional" / "Legit — promotion".
export function LabelChoice({ options = LEGIT_OPTIONS, value, onChange, disabled, ariaLabel = "Correct label" }) {
  const ui = useUi();
  return (
    <div className={ui.choices} role="radiogroup" aria-label={ariaLabel}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            disabled={disabled}
            className={`${ui.choice} ${on ? ui.choiceOn : ""}`}
            onClick={() => onChange(o.value)}
          >
            <span className={`${ui.dot} ${on ? ui.dotOn : ""}`} />
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// Checkbox row with a hover explanation (help / term as in STATUS_HELP).
export function CheckOption({ checked, onChange, disabled, help, term, children }) {
  const ui = useUi();
  return (
    <HelpTip help={help} term={term}>
      <label className={`${ui.check} ${disabled ? ui.checkOff : ""}`}>
        <input type="checkbox" checked={!!checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
        <span>{children}</span>
      </label>
    </HelpTip>
  );
}

// Business context, asked on every case Dismiss and item Mark OK / Approve. Saved on
// the owner (all projects). Required (min length) until the owner has saved context;
// then prefilled and fine as is, but an edit must still meet the minimum.
export const CONTEXT_MIN = 15;
export const CONTEXT_MAX = 600;
export const contextFrom = (saved) => ({ about: saved?.about || "", useCases: saved?.useCases || "" });
const longEnough = (v) => (v?.about || "").trim().length >= CONTEXT_MIN && (v?.useCases || "").trim().length >= CONTEXT_MIN;
const sameAs = (v, saved) =>
  !!(saved?.about && saved?.useCases) &&
  (v?.about || "").trim() === saved.about.trim() &&
  (v?.useCases || "").trim() === saved.useCases.trim();
export const contextReady = (v, saved) => longEnough(v) || sameAs(v, saved);
export const contextBody = (v) => ({ about: (v?.about || "").trim(), useCases: (v?.useCases || "").trim() });

// Owner sender types and industries (owner-settings API values).
export const SENDER_TYPES = [
  { value: "aggregator", label: "Aggregator", description: "Sends on behalf of lenders or clients." },
  { value: "service_provider", label: "Service provider", description: "Runs messaging for client businesses." },
];
export const REASON_MIN = 10;
export const REASON_MAX = 500;
export const INDUSTRIES = [
  { value: "finance", label: "Finance", badge: "Finance" },
  { value: "education", label: "Education", badge: "Education" },
  { value: "healthcare", label: "Healthcare", badge: "Healthcare" },
  { value: "ecommerce", label: "E-commerce", badge: "E-commerce" },
  { value: "travel", label: "Travel", badge: "Travel" },
  { value: "real_estate", label: "Real estate", badge: "Real estate" },
  { value: "marketing_agency", label: "Marketing / agency", badge: "Marketing" },
  { value: "other", label: "Other", badge: "" },
];
// "Finance aggregator", "Education service provider", "Aggregator" (other / no industry).
export const senderLabel = (senderType, industry) => {
  const type = STATUS_HELP[senderType]?.label || sentenceCase(senderType);
  const ind = INDUSTRIES.find((i) => i.value === industry)?.badge;
  return ind ? `${ind} ${type.toLowerCase()}` : type;
};

// Compact "Category" select; an empty value keeps the model's own label.
export function CategorySelect({ value, onChange, disabled, modelLabel }) {
  const ui = useUi();
  const asFlagged = !isHarm(modelLabel);
  return (
    <label className={ui.field}>
      Category
      <select className={ui.select} value={value || ""} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
        {asFlagged ? <option value="">{modelLabel ? `As flagged (${humanizeLabel(modelLabel).toLowerCase()})` : "As flagged"}</option> : null}
        {HARM_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}
